import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import UserAvatar from './UserAvatar';
import ContactHoverCard from './ContactHoverCard';
import './Contacts.css';
import './ContactHoverCard.css';

const Contacts = ({ currentUser }) => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchContacts = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/api/contacts', {
        params: { page: 1, limit: 500, source: 'manual' },
      });
      if (response.data.success) {
        setContacts(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to load contacts');
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError(err.response?.data?.message || 'Error loading contacts. Please try again.');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) fetchContacts();
  }, [currentUser]);

  useEffect(() => {
    const handleContactsUpdated = () => fetchContacts(false);
    window.addEventListener('contactsUpdated', handleContactsUpdated);
    return () => window.removeEventListener('contactsUpdated', handleContactsUpdated);
  }, []);

  const filteredContacts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return contacts;
    return contacts.filter((contact) => {
      const name = String(contact.displayName || contact.username || '').toLowerCase();
      const email = String(contact.email || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [contacts, searchQuery]);

  const handleCompose = (email) => {
    if (!email) return;
    navigate(`/compose?to=${encodeURIComponent(email)}`);
  };

  const handleEditContact = async (contactId, displayName) => {
    if (!contactId) return;
    try {
      const response = await axiosInstance.patch(`/api/contacts/${contactId}`, {
        displayName,
      });
      if (response.data.success) {
        setContacts((prev) =>
          prev.map((entry) => (entry._id === contactId ? response.data.data : entry))
        );
        window.dispatchEvent(new CustomEvent('contactsUpdated'));
      }
    } catch (err) {
      console.error('Error updating contact:', err);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!contactId) return;
    try {
      const response = await axiosInstance.delete(`/api/contacts/${contactId}`);
      if (response.data.success) {
        setContacts((prev) => prev.filter((entry) => entry._id !== contactId));
        window.dispatchEvent(new CustomEvent('contactsUpdated'));
      }
    } catch (err) {
      console.error('Error deleting contact:', err);
    }
  };

  const handleToggleFavorite = async (contact) => {
    if (!contact?._id) return;
    try {
      const response = await axiosInstance.post('/api/contacts/favorite', {
        contactId: contact._id,
        isFavorite: !contact.isFavorite,
      });
      if (response.data.success) {
        setContacts((prev) =>
          prev.map((entry) => (entry._id === contact._id ? response.data.data : entry))
        );
      }
    } catch (err) {
      console.error('Error updating favorite:', err);
    }
  };

  return (
    <div className="contacts-container">
      <div className="contacts-header">
        <div className="contacts-header-copy">
          <h2>Contacts</h2>
          <p className="contacts-count">
            {loading ? 'Loading...' : `${filteredContacts.length} contact${filteredContacts.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <input
          type="search"
          className="contacts-search"
          placeholder="Search contacts"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
        />
      </div>

      <div className="contacts-body">
        {loading && <div className="contacts-state">Loading contacts...</div>}
        {!loading && error && <div className="contacts-state contacts-error">{error}</div>}
        {!loading && !error && filteredContacts.length === 0 && (
          <div className="contacts-state">
            <p>
              {searchQuery.trim()
                ? 'No contacts match your search.'
                : 'No contacts yet.'}
            </p>
            {!searchQuery.trim() && (
              <p className="contacts-state-hint">
                Hover over a recipient in compose mail and click <strong>Add contact</strong>—those contacts will appear here.
              </p>
            )}
          </div>
        )}

        {!loading && !error && filteredContacts.length > 0 && (
          <div className="contacts-list">
            {filteredContacts.map((contact) => {
              const displayName = contact.displayName || contact.username || contact.email;
              return (
                <div key={contact._id || contact.email} className="contacts-list-item">
                  <div className="contact-list-hover-wrap">
                    <button
                      type="button"
                      className="contacts-list-main"
                      onClick={() => handleCompose(contact.email)}
                    >
                      <UserAvatar user={contact} size="md" className="contacts-list-avatar" />
                      <span className="contacts-list-copy">
                        <span className="contacts-list-name">{displayName}</span>
                        <span className="contacts-list-email">{contact.email}</span>
                      </span>
                    </button>
                    <ContactHoverCard
                      contact={contact}
                      contactId={contact._id}
                      isSaved
                      onEditContact={(name) => handleEditContact(contact._id, name)}
                      onSendMail={() => handleCompose(contact.email)}
                    />
                  </div>
                  <div className="contacts-list-actions">
                    <button
                      type="button"
                      className={`contacts-fav-btn${contact.isFavorite ? ' active' : ''}`}
                      onClick={() => handleToggleFavorite(contact)}
                      title={contact.isFavorite ? 'Remove favorite' : 'Mark favorite'}
                      aria-label={contact.isFavorite ? 'Remove favorite' : 'Mark favorite'}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="contacts-delete-btn"
                      onClick={() => handleDeleteContact(contact._id)}
                      title="Remove contact"
                      aria-label="Remove contact"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Contacts;
